import { AlertNoticeProps } from '../components';
import { createStore, HoneycombStore, useStore } from '../store';
import { randomId } from '../utils';

export type ToastPosition = 'top-start' | 'top-end' | 'top-center' | 'bottom-start' | 'bottom-end' | 'bottom-center';

export interface ToastData extends Omit<AlertNoticeProps, 'onDismiss'> {
    /**
     * The unique id of the toast. Can be used to close or update a toast.
     * @default 5
     */
    id?: string;

    /**
     * The position of this toast. If not set, this will fallback to the default position set on the Toasts component.
     */
    position?: ToastPosition;

    /**
     * The message content which will be displayed on the toast.
     */
    message: React.ReactNode;

    /**
     * The auto-close timeout (in ms) for this toast. If not set, this will fallback to the value set on the Toasts component.
     * If a number, the timeout timeout will be in milliseconds.
     * If `false`, there will be no auto-close timeout.
     */
    autoClose?: boolean | number;

    /**
     * Callback fired when a the toast opens.
     */
    onOpen?: (props: ToastData) => void;

    /**
     * Callback fired when a the toast closes.
     */
    onClose?: (props: ToastData) => void;
}

export interface ToastsState {
    toasts: ToastData[];
    queue: ToastData[];
    defaultPosition: ToastPosition;
    limit: number;
}

export type ToastsStore = HoneycombStore<ToastsState>;

export const createToastsStore = () =>
    createStore<ToastsState>({
        toasts: [],
        queue: [],
        defaultPosition: 'bottom-end',
        limit: 5,
    });

export const toastsStore = createToastsStore();
export const useToasts = (store: ToastsStore = toastsStore) => useStore(store);

function getDistributedToasts(data: ToastData[], defaultPosition: ToastPosition, limit: number) {
    const queue: ToastData[] = [];
    const toasts: ToastData[] = [];
    const count: Record<string, number> = {};

    data.forEach((item) => {
        const position = item.position || defaultPosition;
        count[position] = count[position] || 0;
        count[position] += 1;

        if (count[position] <= limit) {
            toasts.push(item);
        } else {
            queue.push(item);
        }
    });

    return { toasts, queue };
}

export function updateToastsState(store: ToastsStore, update: (toasts: ToastData[]) => ToastData[]) {
    const state = store.getState();
    const toasts = update([...state.toasts, ...state.queue]);
    const updated = getDistributedToasts(toasts, state.defaultPosition, state.limit);

    store.setState({
        toasts: updated.toasts,
        queue: updated.queue,
        limit: state.limit,
        defaultPosition: state.defaultPosition,
    });
}

export function showToast(toast: ToastData, store: ToastsStore = toastsStore) {
    const id = toast.id || randomId();

    updateToastsState(store, (toasts) => {
        if (toast.id && toasts.some((n) => n.id === toast.id)) {
            return toasts;
        }

        return [...toasts, { ...toast, id }];
    });

    return id;
}

export function hideToast(id: string, store: ToastsStore = toastsStore) {
    updateToastsState(store, (toasts) =>
        toasts.filter((toast) => {
            if (toast.id === id) {
                toast.onClose?.(toast);
                return false;
            }
            return true;
        })
    );

    return id;
}

export function updateToast(toast: ToastData, store: ToastsStore = toastsStore) {
    updateToastsState(store, (toasts) =>
        toasts.map((item) => {
            if (item.id === toast.id) {
                return { ...item, ...toast };
            }

            return item;
        })
    );

    return toast.id;
}

export function cleanToasts(store: ToastsStore = toastsStore) {
    updateToastsState(store, () => []);
}

export function cleanToastsQueue(store: ToastsStore = toastsStore) {
    updateToastsState(store, (toasts) => toasts.slice(0, store.getState().limit));
}

export const toasts = {
    show: showToast,
    hide: hideToast,
    update: updateToast,
    clean: cleanToasts,
    cleanQueue: cleanToastsQueue,
    updateState: updateToastsState,
} as const;
